import { useState } from 'react';
import { useSearchParams } from 'react-router';
import { Icon, type IconName } from '../components/Icon.tsx';
import { Shell } from '../components/Shell.tsx';
import { Button, Confirm, Dialog, EmptyState, Field, IconButton, Skeleton, StatusBadge, Switch, type Tone, TableWrap } from '../components/ui.tsx';
import { locale, t, tn, type MessageKey } from '../i18n/index.ts';
import { action } from '../lib/api.ts';
import { day as dayText, money, parseMoney } from '../lib/format.ts';
import { useSession } from '../lib/session.tsx';
import { useUi } from '../lib/ui-state.tsx';
import { useAction, useSave } from './manage/common.tsx';
import { downloadCsv, Kpi, plain, Table } from './Reports.tsx';

/**
 * Lunch: everyone orders today's lunch from the business's vendors before each vendor's time, and
 * pays from their lunch account. Managers send one order per vendor, keep the menus and top up the
 * accounts.
 */

type OState = 'ordered' | 'sent' | 'received' | 'cancelled';
type Tab = 'order' | 'day' | 'accounts' | 'vendors' | 'report';

interface Dish {
  id: string;
  category: string | null;
  name: string;
  description: string | null;
  price: number;
}
interface MyOrder {
  id: string;
  vendor: string;
  vendor_name: string;
  name: string;
  qty: number;
  total: number;
  note: string | null;
  state: OState;
}
interface Overview {
  today: string;
  now: string;
  balance: number;
  overdraft: number | null;
  message: string | null;
  vendors: {
    id: string;
    name: string;
    phone: string | null;
    address: string | null;
    note: string | null;
    deadline: string;
    open: boolean;
    products: Dish[];
  }[];
  favourites: string[];
  mine: MyOrder[];
  can: { manage: boolean };
}
const O_STATE: Record<OState, { tone: Tone; icon: IconName }> = {
  ordered: { tone: 'info', icon: 'clock' },
  sent: { tone: 'primary', icon: 'send' },
  received: { tone: 'ok', icon: 'checkCircle' },
  cancelled: { tone: 'neutral', icon: 'x' },
};
const DAYS = [1, 2, 3, 4, 5, 6, 7];
const dayName = (n: number, style: 'short' | 'long' = 'short') => new Intl.DateTimeFormat(locale(), { weekday: style, timeZone: 'UTC' }).format(new Date(Date.UTC(2026, 9, 25 + n)));

export function Lunch() {
  const [params, setParams] = useSearchParams();
  const { data: o, reload } = useAction<Overview>('lunch.overview');
  const [version, setVersion] = useState(0);
  const [account, setAccount] = useState<string | null>(null);
  const manage = !!o?.can.manage;
  const tabs: { key: Tab; icon: IconName }[] = manage
    ? [
        { key: 'order', icon: 'chef' },
        { key: 'day', icon: 'send' },
        { key: 'accounts', icon: 'cash' },
        { key: 'vendors', icon: 'list' },
        { key: 'report', icon: 'chart' },
      ]
    : [];
  const tab = (tabs.some((x) => x.key === params.get('tab')) ? params.get('tab') : 'order') as Tab;
  const setTab = (k: Tab) => {
    const p = new URLSearchParams(params);
    if (k === 'order') p.delete('tab');
    else p.set('tab', k);
    setParams(p, { replace: true });
  };
  const changed = async () => {
    setVersion((v) => v + 1);
    await reload();
  };
  const waiting = o?.mine.filter((m) => m.state !== 'cancelled').length ?? 0;
  return (
    <Shell>
      <div className="page reports lu-page">
        <div className="page-head">
          <div>
            <h1>{t('lunch.title')}</h1>
            <p className="muted">{o ? t('lunch.subtitle', { day: dayText(o.today) }) : ' '}</p>
          </div>
          {o ? (
            <button type="button" className={`lu-balance${o.balance < 0 ? ' neg' : ''}`} onClick={() => setAccount('me')}>
              <span className="muted small">{t('lunch.my_balance')}</span>
              <strong>{money(o.balance)}</strong>
              <span className="small lu-balance-link">
                {t('lunch.see_account')} <Icon name="chevronRight" size={14} />
              </span>
            </button>
          ) : null}
        </div>
        {tabs.length ? (
          <div className="segmented tabs" role="tablist" aria-label={t('lunch.sections')}>
            {tabs.map((x) => (
              <button key={x.key} role="tab" aria-selected={tab === x.key} className={tab === x.key ? 'active' : ''} onClick={() => setTab(x.key)}>
                <Icon name={x.icon} size={16} />
                {t(`lunch.tab_${x.key}` as MessageKey)}
              </button>
            ))}
          </div>
        ) : null}
        {!o ? (
          <Skeleton h={300} r="var(--radius-lg)" />
        ) : tab === 'order' ? (
          <OrderTab o={o} waiting={waiting} onChange={changed} />
        ) : tab === 'day' ? (
          <DayTab key={version} today={o.today} onChange={changed} />
        ) : tab === 'accounts' ? (
          <AccountsTab key={version} onOpen={(id) => setAccount(id)} />
        ) : tab === 'vendors' ? (
          <VendorsTab onChange={changed} />
        ) : (
          <ReportTab today={o.today} />
        )}
      </div>
      {account ? <AccountDialog user={account === 'me' ? null : account} manage={manage && account !== 'me'} onClose={() => setAccount(null)} onChange={changed} /> : null}
    </Shell>
  );
}

// ---- ordering ---------------------------------------------------------------------------

function OrderTab({ o, waiting, onChange }: { o: Overview; waiting: number; onChange: () => Promise<void> }) {
  const [pick, setPick] = useState<{
    dish: Dish;
    vendor: Overview['vendors'][number];
  } | null>(null);
  const [drop, setDrop] = useState<MyOrder | null>(null);
  const { busy, run } = useSave();
  const fav = new Set(o.favourites);
  const open = o.vendors.filter((v) => v.open);
  return (
    <div className="lu-order">
      <div className="lu-menu">
        {o.message ? (
          <p className="callout lu-message">
            <Icon name="bell" size={16} /> {o.message}
          </p>
        ) : null}
        {!o.vendors.length ? (
          <section className="panel">
            <EmptyState icon="chef" title={t('lunch.no_vendors_today')} body={o.can.manage ? t('lunch.no_vendors_today_manager') : t('lunch.no_vendors_today_body')} />
          </section>
        ) : (
          o.vendors.map((v) => {
            const cats = [...new Set(v.products.map((p) => p.category ?? ''))];
            return (
              <section key={v.id} className={`panel lu-vendor${v.open ? '' : ' closed'}`}>
                <div className="lu-vendor-head">
                  <div>
                    <h2>{v.name}</h2>
                    <span className="muted small">{[v.address, v.note].filter(Boolean).join(' · ')}</span>
                  </div>
                  {v.open ? (
                    <StatusBadge tone="ok" icon="clock" label={t('lunch.order_by', { time: v.deadline })} />
                  ) : (
                    <StatusBadge tone="neutral" icon="clock" label={t('lunch.closed_at', { time: v.deadline })} />
                  )}
                </div>
                {!v.products.length ? (
                  <p className="muted">{t('lunch.no_dishes')}</p>
                ) : (
                  cats.map((c) => (
                    <div key={c} className="lu-cat">
                      {c ? <h3 className="lu-cat-name">{c}</h3> : null}
                      <ul className="lu-dishes">
                        {v.products
                          .filter((p) => (p.category ?? '') === c)
                          .map((p) => (
                            <li key={p.id}>
                              <button type="button" className="lu-dish" disabled={!v.open} onClick={() => setPick({ dish: p, vendor: v })}>
                                <span className="lu-dish-text">
                                  <strong>
                                    {p.name}
                                    {fav.has(p.id) ? (
                                      <span className="lu-fav" title={t('lunch.you_often')}>
                                        <Icon name="star" size={14} />
                                        <span className="sr-only">{t('lunch.you_often')}</span>
                                      </span>
                                    ) : null}
                                  </strong>
                                  {p.description ? <span className="muted small">{p.description}</span> : null}
                                </span>
                                <span className="lu-price">{money(p.price)}</span>
                                {v.open ? (
                                  <span className="lu-add" aria-hidden="true">
                                    <Icon name="plus" size={16} />
                                  </span>
                                ) : null}
                              </button>
                            </li>
                          ))}
                      </ul>
                    </div>
                  ))
                )}
              </section>
            );
          })
        )}
      </div>
      <aside className="lu-side">
        <section className="panel lu-mine">
          <div className="panel-head">
            <h2>{t('lunch.your_lunch')}</h2>
            {waiting ? <span className="muted small">{money(o.mine.filter((m) => m.state !== 'cancelled').reduce((s, m) => s + m.total, 0))}</span> : null}
          </div>
          {!o.mine.length ? (
            <p className="muted">{open.length ? t('lunch.nothing_yet') : t('lunch.nothing_closed')}</p>
          ) : (
            <ul className="lu-mine-list">
              {o.mine.map((m) => {
                const v = o.vendors.find((x) => x.id === m.vendor);
                const canCancel = m.state === 'ordered' && !!v?.open;
                return (
                  <li key={m.id} className={m.state === 'cancelled' ? 'cancelled' : undefined}>
                    <span className="lu-mine-text">
                      <strong>
                        {m.qty > 1 ? `${m.qty} × ` : ''}
                        {m.name}
                      </strong>
                      <span className="muted small">{[m.vendor_name, m.note].filter(Boolean).join(' · ')}</span>
                    </span>
                    <span className="lu-mine-end">
                      <span className="lu-price">{money(m.total)}</span>
                      {m.state !== 'ordered' || !canCancel ? <StatusBadge tone={O_STATE[m.state].tone} icon={O_STATE[m.state].icon} label={t(`lunch.state_${m.state}` as MessageKey)} /> : null}
                      {canCancel ? <IconButton icon="x" label={t('lunch.cancel_order')} onClick={() => setDrop(m)} /> : null}
                    </span>
                  </li>
                );
              })}
            </ul>
          )}
        </section>
        <p className="muted small lu-side-note">
          {o.overdraft === null ? t('lunch.no_limit_note') : o.overdraft ? t('lunch.overdraft_note', { amount: money(o.overdraft) }) : t('lunch.no_overdraft_note')}
        </p>
      </aside>
      {pick ? <OrderDialog pick={pick} balance={o.balance} overdraft={o.overdraft} onClose={() => setPick(null)} onDone={onChange} /> : null}
      {drop ? (
        <Confirm
          title={t('lunch.cancel_title')}
          body={t('lunch.cancel_body', {
            what: drop.name,
            amount: money(drop.total),
          })}
          confirm={t('lunch.cancel_order')}
          danger
          busy={busy}
          onClose={() => setDrop(null)}
          onConfirm={() =>
            void run(async () => {
              await action('lunch.cancel', { id: drop.id });
              setDrop(null);
              await onChange();
            }, t('lunch.cancelled_done'))
          }
        />
      ) : null}
    </div>
  );
}

function Stepper({ value, onChange, min = 1, max = 20 }: { value: number; onChange: (n: number) => void; min?: number; max?: number }) {
  return (
    <div className="lu-stepper" role="group" aria-label={t('lunch.qty')}>
      <IconButton icon="minus" label={t('lunch.less')} onClick={() => onChange(Math.max(min, value - 1))} disabled={value <= min} />
      <output aria-live="polite">{value}</output>
      <IconButton icon="plus" label={t('lunch.more')} onClick={() => onChange(Math.min(max, value + 1))} disabled={value >= max} />
    </div>
  );
}

function OrderDialog({
  pick,
  balance,
  overdraft,
  onClose,
  onDone,
}: {
  pick: { dish: Dish; vendor: Overview['vendors'][number] };
  balance: number;
  overdraft: number | null;
  onClose: () => void;
  onDone: () => Promise<void>;
}) {
  const { busy, run } = useSave();
  const [qty, setQty] = useState(1);
  const [note, setNote] = useState('');
  const total = pick.dish.price * qty;
  const after = balance - total;
  const short = overdraft !== null && after < -overdraft;
  const save = () =>
    void run(
      async () => {
        await action('lunch.order', {
          product: pick.dish.id,
          qty,
          note: note.trim() || null,
        });
        onClose();
        await onDone();
      },
      t('lunch.ordered_done', { what: pick.dish.name }),
    );
  return (
    <Dialog
      title={pick.dish.name}
      onClose={onClose}
      size="sm"
      footer={
        <span className="foot-actions">
          <Button variant="ghost" onClick={onClose}>
            {t('common.cancel')}
          </Button>
          <Button variant="primary" icon="check" busy={busy} disabled={short} onClick={save}>
            {t('lunch.order_for', { amount: money(total) })}
          </Button>
        </span>
      }
    >
      <div className="form-stack">
        <p className="muted">
          {pick.vendor.name} · {t('lunch.order_by', { time: pick.vendor.deadline })}
          {pick.dish.description ? (
            <>
              <br />
              {pick.dish.description}
            </>
          ) : null}
        </p>
        <Field label={t('lunch.qty')}>
          <Stepper value={qty} onChange={setQty} />
        </Field>
        <Field label={t('lunch.note')} hint={t('lunch.note_hint')}>
          <input className="input" value={note} maxLength={200} onChange={(e) => setNote(e.target.value)} data-autofocus />
        </Field>
        <dl className="lu-after">
          <dt>{t('lunch.balance_now')}</dt>
          <dd>{money(balance)}</dd>
          <dt>{t('lunch.balance_after')}</dt>
          <dd className={after < 0 ? 'text-danger' : undefined}>{money(after)}</dd>
        </dl>
        {short ? (
          <p className="callout callout-danger" role="alert">
            <Icon name="alert" size={16} /> {t('lunch.short')}
          </p>
        ) : null}
      </div>
    </Dialog>
  );
}

// ---- my account / someone's account ------------------------------------------------------

function AccountDialog({ user, manage, onClose, onChange }: { user: string | null; manage: boolean; onClose: () => void; onChange: () => Promise<void> }) {
  const { data: a, reload } = useAction<{
    name: string;
    user: string;
    balance: number;
    spent_month: number;
    moves: {
      id: string;
      day: string;
      amount: number;
      kind: 'topup' | 'order' | 'refund' | 'correction';
      note: string | null;
      by: string | null;
    }[];
  }>('lunch.account', { user });
  const [adding, setAdding] = useState(false);
  return (
    <Dialog title={user ? t('lunch.account_of', { name: a?.name ?? '' }) : t('lunch.my_account')} onClose={onClose} size="md">
      {!a ? (
        <Skeleton h={240} r="var(--radius-lg)" />
      ) : (
        <div className="form-stack">
          <div className="item-summary">
            <div>
              <span className="muted small">{t('lunch.balance')}</span>
              <strong className={a.balance < 0 ? 'text-danger' : undefined}>{money(a.balance)}</strong>
            </div>
            <div>
              <span className="muted small">{t('lunch.spent_month')}</span>
              <strong>{money(a.spent_month)}</strong>
            </div>
            {manage ? (
              <div className="lu-add-money">
                <Button size="sm" variant="primary" icon="plus" onClick={() => setAdding(true)}>
                  {t('lunch.add_money')}
                </Button>
              </div>
            ) : null}
          </div>
          {!user && a.balance < 0 ? <p className="callout">{t('lunch.pay_back_hint')}</p> : null}
          <TableWrap>
            <Table
              head={[t('lunch.day'), t('lunch.what'), t('lunch.amount')]}
              empty={t('lunch.no_moves')}
              rows={a.moves.map((m) => ({
                key: m.id,
                cells: [
                  dayText(m.day),
                  <span key="w" className="lu-move">
                    <strong>{t(`lunch.kind_${m.kind}` as MessageKey)}</strong>
                    <span className="muted small">{[m.note, m.by ? t('lunch.by', { name: m.by }) : null].filter(Boolean).join(' · ')}</span>
                  </span>,
                  <span key="a" className={m.amount < 0 ? undefined : 'text-ok'}>
                    {m.amount > 0 ? '+' : ''}
                    {money(m.amount)}
                  </span>,
                ],
              }))}
            />
          </TableWrap>
        </div>
      )}
      {adding && a ? (
        <MoneyDialog
          user={a.user}
          name={a.name}
          onClose={() => setAdding(false)}
          onDone={async () => {
            setAdding(false);
            await reload();
            await onChange();
          }}
        />
      ) : null}
    </Dialog>
  );
}

function MoneyDialog({ user, name, onClose, onDone }: { user: string; name: string; onClose: () => void; onDone: () => Promise<void> }) {
  const { busy, run } = useSave();
  const [kind, setKind] = useState<'topup' | 'correction'>('topup');
  const [amount, setAmount] = useState('');
  const [minus, setMinus] = useState(false);
  const [note, setNote] = useState('');
  const [tried, setTried] = useState(false);
  const value = parseMoney(amount);
  const errors = {
    amount: !value ? t('lunch.e_amount') : null,
    note: kind === 'correction' && !note.trim() ? t('lunch.e_why') : null,
  };
  const save = () => {
    setTried(true);
    if (errors.amount || errors.note) return;
    const signed = kind === 'correction' && minus ? -value! : value!;
    void run(
      async () => {
        await action('lunch.add_money', {
          user,
          amount: signed,
          kind,
          note: note.trim() || null,
        });
        await onDone();
      },
      kind === 'topup' ? t('lunch.topped_up', { name, amount: money(signed) }) : t('lunch.corrected', { name }),
    );
  };
  return (
    <Dialog
      title={t('lunch.money_for', { name })}
      onClose={onClose}
      size="sm"
      footer={
        <span className="foot-actions">
          <Button variant="ghost" onClick={onClose}>
            {t('common.cancel')}
          </Button>
          <Button variant="primary" icon="check" busy={busy} onClick={save}>
            {t('common.save')}
          </Button>
        </span>
      }
    >
      <div className="form-stack">
        <div className="segmented subtle" role="radiogroup" aria-label={t('lunch.what')}>
          {(['topup', 'correction'] as const).map((k) => (
            <button key={k} type="button" role="radio" aria-checked={kind === k} className={kind === k ? 'active' : ''} onClick={() => setKind(k)}>
              {t(`lunch.kind_${k}` as MessageKey)}
            </button>
          ))}
        </div>
        <Field label={t('lunch.amount')} error={tried ? errors.amount : null} hint={kind === 'topup' ? t('lunch.topup_hint') : undefined} required>
          <input className="input" inputMode="decimal" value={amount} onChange={(e) => setAmount(e.target.value)} data-autofocus />
        </Field>
        {kind === 'correction' ? (
          <div className="setting-row">
            <div>
              <strong>{t('lunch.take_off')}</strong>
              <p className="muted">{t('lunch.take_off_hint')}</p>
            </div>
            <Switch checked={minus} onChange={setMinus} label={t('lunch.take_off')} />
          </div>
        ) : null}
        <Field label={t('lunch.note')} error={tried ? errors.note : null} required={kind === 'correction'}>
          <input className="input" value={note} maxLength={200} onChange={(e) => setNote(e.target.value)} placeholder={kind === 'topup' ? t('lunch.cash') : ''} />
        </Field>
      </div>
    </Dialog>
  );
}

// ---- managers: the day's orders -----------------------------------------------------------

interface DayData {
  day: string;
  today: string;
  now: string;
  people: number;
  total: number;
  vendors: {
    id: string;
    name: string;
    phone: string | null;
    email: string | null;
    deadline: string;
    closed: boolean;
    state: 'ordered' | 'sent' | 'received' | null;
    total: number;
    lines: { name: string; qty: number; notes: string[] }[];
    orders: {
      id: string;
      person: string;
      name: string;
      qty: number;
      note: string | null;
      total: number;
      state: OState;
    }[];
  }[];
}

function orderText(v: DayData['vendors'][number], day: string, business: string) {
  return [
    t('lunch.text_head', { business, day: dayText(day) }),
    '',
    ...v.lines.map((l) => `${l.qty} × ${l.name}${l.notes.length ? ` (${l.notes.join('; ')})` : ''}`),
    '',
    t('lunch.text_total', { amount: money(v.total) }),
  ].join('\n');
}

function DayTab({ today, onChange }: { today: string; onChange: () => Promise<void> }) {
  const [d, setD] = useState(today);
  const { data, reload } = useAction<DayData>('lunch.day', { day: d });
  const { toast } = useUi();
  const { busy, run } = useSave();
  const [drop, setDrop] = useState<DayData['vendors'][number]['orders'][number] | null>(null);
  const business = useSession().tenant?.name ?? '';
  const mark = (vendor: string, state: 'sent' | 'received') =>
    void run(
      async () => {
        await action('lunch.set_day_state', { vendor, day: d, state });
        await reload();
        await onChange();
      },
      state === 'sent' ? t('lunch.sent_done') : t('lunch.received_done'),
    );
  const copy = async (text: string) => {
    try {
      await navigator.clipboard.writeText(text);
      toast(t('lunch.copied'), 'ok');
    } catch {
      toast(t('lunch.copy_failed'), 'danger');
    }
  };
  return (
    <div className="lu-day">
      <div className="lu-day-tools">
        <input className="input lu-date" type="date" value={d} max={addDaysIso(today, 7)} onChange={(e) => e.target.value && setD(e.target.value)} aria-label={t('lunch.day')} />
        {d !== today ? (
          <Button size="sm" variant="ghost" onClick={() => setD(today)}>
            {t('lunch.today')}
          </Button>
        ) : null}
        {data ? (
          <span className="muted">
            {data.vendors.length
              ? t('lunch.day_summary', {
                  people: data.people,
                  amount: money(data.total),
                })
              : ''}
          </span>
        ) : null}
      </div>
      {!data ? (
        <Skeleton h={240} r="var(--radius-lg)" />
      ) : !data.vendors.length ? (
        <section className="panel">
          <EmptyState icon="chef" title={t('lunch.no_orders_day')} body={d === today ? t('lunch.no_orders_day_body') : undefined} />
        </section>
      ) : (
        data.vendors.map((v) => {
          const text = orderText(v, data.day, business);
          return (
            <section key={v.id} className="panel lu-send">
              <div className="lu-vendor-head">
                <div>
                  <h2>{v.name}</h2>
                  <span className="muted small">{[v.closed ? t('lunch.closed_at', { time: v.deadline }) : t('lunch.open_until', { time: v.deadline }), v.phone].filter(Boolean).join(' · ')}</span>
                </div>
                {v.state ? <StatusBadge tone={O_STATE[v.state].tone} icon={O_STATE[v.state].icon} label={t(`lunch.vstate_${v.state}` as MessageKey)} /> : null}
              </div>
              {v.lines.length ? (
                <div className="lu-send-body">
                  <ul className="lu-lines">
                    {v.lines.map((l, i) => (
                      <li key={i}>
                        <strong className="lu-qty">{l.qty} ×</strong>
                        <span>
                          {l.name}
                          {l.notes.length ? <span className="muted small"> · {l.notes.join('; ')}</span> : null}
                        </span>
                      </li>
                    ))}
                    <li className="lu-lines-total">
                      <span>{t('lunch.total')}</span>
                      <strong>{money(v.total)}</strong>
                    </li>
                  </ul>
                  <div className="lu-send-acts">
                    {v.state === 'ordered' ? (
                      <>
                        <Button size="sm" icon="copy" onClick={() => void copy(text)}>
                          {t('lunch.copy_text')}
                        </Button>
                        {v.phone ? (
                          <a className="btn btn-secondary btn-sm" href={`tel:${v.phone.replace(/\s/g, '')}`}>
                            <Icon name="phone" size={16} /> {t('lunch.call')}
                          </a>
                        ) : null}
                        {v.email ? (
                          <a
                            className="btn btn-secondary btn-sm"
                            href={`mailto:${v.email}?subject=${encodeURIComponent(t('lunch.mail_subject', { day: dayText(data.day) }))}&body=${encodeURIComponent(text)}`}
                          >
                            <Icon name="mail" size={16} /> {t('lunch.email')}
                          </a>
                        ) : null}
                        <Button size="sm" variant="primary" icon="send" busy={busy} onClick={() => mark(v.id, 'sent')}>
                          {t('lunch.mark_sent')}
                        </Button>
                      </>
                    ) : v.state === 'sent' ? (
                      <Button size="sm" variant="success" icon="checkCircle" busy={busy} onClick={() => mark(v.id, 'received')}>
                        {t('lunch.mark_received')}
                      </Button>
                    ) : null}
                  </div>
                  {v.state === 'ordered' && !v.closed ? <p className="muted small">{t('lunch.still_open', { time: v.deadline })}</p> : null}
                </div>
              ) : null}
              <details className="lu-people">
                <summary>{tn('lunch.n_orders', v.orders.length)}</summary>
                <ul>
                  {v.orders.map((x) => (
                    <li key={x.id} className={x.state === 'cancelled' ? 'cancelled' : undefined}>
                      <span>
                        <strong>{x.person}</strong> · {x.qty > 1 ? `${x.qty} × ` : ''}
                        {x.name}
                        {x.note ? <span className="muted small"> · {x.note}</span> : null}
                      </span>
                      <span className="lu-mine-end">
                        <span className="lu-price">{money(x.total)}</span>
                        {x.state === 'cancelled' ? (
                          <StatusBadge tone="neutral" icon="x" label={t('lunch.state_cancelled')} />
                        ) : x.state !== 'received' ? (
                          <IconButton icon="x" label={t('lunch.cancel_order')} onClick={() => setDrop(x)} />
                        ) : null}
                      </span>
                    </li>
                  ))}
                </ul>
              </details>
            </section>
          );
        })
      )}
      {drop ? (
        <Confirm
          title={t('lunch.cancel_for', { name: drop.person })}
          body={t('lunch.cancel_for_body', {
            what: drop.name,
            amount: money(drop.total),
          })}
          confirm={t('lunch.cancel_order')}
          danger
          busy={busy}
          onClose={() => setDrop(null)}
          onConfirm={() =>
            void run(async () => {
              await action('lunch.cancel', { id: drop.id });
              setDrop(null);
              await reload();
              await onChange();
            }, t('lunch.cancelled_done'))
          }
        />
      ) : null}
    </div>
  );
}
function addDaysIso(d: string, n: number) {
  const x = new Date(`${d}T00:00:00Z`);
  x.setUTCDate(x.getUTCDate() + n);
  return x.toISOString().slice(0, 10);
}

// ---- managers: accounts -------------------------------------------------------------------

function AccountsTab({ onOpen }: { onOpen: (id: string) => void }) {
  const { data } = useAction<{
    people: {
      id: string;
      name: string;
      balance: number;
      last_topup: string | null;
      last_order: string | null;
    }[];
    overdraft: number | null;
    owed: number;
    held: number;
  }>('lunch.accounts');
  if (!data) return <Skeleton h={240} r="var(--radius-lg)" />;
  const below = data.people.filter((p) => p.balance < 0).length;
  return (
    <>
      <section className="rep-kpis" aria-label={t('lunch.accounts_glance')}>
        <Kpi label={t('lunch.k_held')} value={money(data.held)} hint={t('lunch.k_held_hint')} />
        <Kpi label={t('lunch.k_owed')} value={money(data.owed)} hint={below ? tn('lunch.k_owed_hint', below) : t('lunch.k_owed_none')} warn={below > 0} />
        <Kpi label={t('lunch.k_limit')} value={data.overdraft === null ? t('lunch.no_limit') : money(data.overdraft)} hint={t('lunch.k_limit_hint')} />
      </section>
      <section className="panel">
        <TableWrap>
          <Table
            head={[t('lunch.person'), t('lunch.balance'), t('lunch.last_topup'), t('lunch.last_order')]}
            phoneHide={[2, 3]}
            rows={data.people.map((p) => ({
              key: p.id,
              onClick: () => onOpen(p.id),
              cells: [
                p.name,
                <strong key="b" className={p.balance < 0 ? 'text-danger' : undefined}>
                  {money(p.balance)}
                </strong>,
                p.last_topup ? dayText(p.last_topup) : '—',
                p.last_order ? dayText(p.last_order) : '—',
              ],
            }))}
          />
        </TableWrap>
      </section>
    </>
  );
}

// ---- managers: vendors and menus ----------------------------------------------------------

interface VendorFull {
  id: string;
  name: string;
  phone: string | null;
  email: string | null;
  address: string | null;
  deadline: string;
  days: number[];
  note: string | null;
  active: boolean;
  orders_30: number;
  products: (Dish & { active: boolean; vendor: string })[];
}

function VendorsTab({ onChange }: { onChange: () => Promise<void> }) {
  const { data, reload } = useAction<{
    vendors: VendorFull[];
    settings: { overdraft: number | null; message: string | null };
  }>('lunch.vendors');
  const [form, setForm] = useState<
    | { kind: 'vendor'; v: VendorFull | null }
    | {
        kind: 'dish';
        vendor: VendorFull;
        dish: VendorFull['products'][number] | null;
      }
    | { kind: 'settings' }
    | null
  >(null);
  const done = async () => {
    setForm(null);
    await reload();
    await onChange();
  };
  if (!data) return <Skeleton h={240} r="var(--radius-lg)" />;
  return (
    <div className="lu-vendors">
      <div className="lu-day-tools">
        <Button variant="primary" icon="plus" onClick={() => setForm({ kind: 'vendor', v: null })}>
          {t('lunch.add_vendor')}
        </Button>
        <Button icon="settings" onClick={() => setForm({ kind: 'settings' })}>
          {t('lunch.settings')}
        </Button>
      </div>
      {!data.vendors.length ? (
        <section className="panel">
          <EmptyState icon="chef" title={t('lunch.no_vendors')} body={t('lunch.no_vendors_body')} />
        </section>
      ) : (
        data.vendors.map((v) => (
          <section key={v.id} className={`panel lu-vendor${v.active ? '' : ' closed'}`}>
            <div className="lu-vendor-head">
              <div>
                <h2>{v.name}</h2>
                <span className="muted small">
                  {[t('lunch.order_by', { time: v.deadline }), v.days.length === 7 ? t('lunch.every_day') : v.days.map((n) => dayName(n)).join(', '), v.phone, v.email].filter(Boolean).join(' · ')}
                </span>
              </div>
              <span className="lu-row-acts">
                {!v.active ? <StatusBadge tone="neutral" icon="archive" label={t('lunch.paused')} /> : <span className="muted small">{tn('lunch.n_orders_30', v.orders_30)}</span>}
                <IconButton icon="edit" label={t('lunch.edit_vendor')} onClick={() => setForm({ kind: 'vendor', v })} />
              </span>
            </div>
            <TableWrap>
              <Table
                head={[t('lunch.dish'), t('lunch.category'), t('lunch.price'), '']}
                phoneHide={[1]}
                empty={t('lunch.no_dishes')}
                rows={v.products.map((p) => ({
                  key: p.id,
                  onClick: () => setForm({ kind: 'dish', vendor: v, dish: p }),
                  cells: [
                    <span key="n" className={`lu-move${p.active ? '' : ' off'}`}>
                      <strong>{p.name}</strong>
                      {p.description ? <span className="muted small">{p.description}</span> : null}
                    </span>,
                    p.category ?? '',
                    money(p.price),
                    p.active ? '' : <StatusBadge key="s" tone="neutral" icon="archive" label={t('lunch.off_menu')} />,
                  ],
                }))}
              />
            </TableWrap>
            <div>
              <Button size="sm" icon="plus" onClick={() => setForm({ kind: 'dish', vendor: v, dish: null })}>
                {t('lunch.add_dish')}
              </Button>
            </div>
          </section>
        ))
      )}
      {form?.kind === 'vendor' ? <VendorForm initial={form.v} onClose={() => setForm(null)} onSaved={done} /> : null}
      {form?.kind === 'dish' ? <DishForm vendor={form.vendor} initial={form.dish} onClose={() => setForm(null)} onSaved={done} /> : null}
      {form?.kind === 'settings' ? <SettingsForm initial={data.settings} onClose={() => setForm(null)} onSaved={done} /> : null}
    </div>
  );
}

function Foot({ busy, onClose, onSave }: { busy: boolean; onClose: () => void; onSave: () => void }) {
  return (
    <span className="foot-actions">
      <Button variant="ghost" onClick={onClose}>
        {t('common.cancel')}
      </Button>
      <Button variant="primary" icon="check" busy={busy} onClick={onSave}>
        {t('common.save')}
      </Button>
    </span>
  );
}

function VendorForm({ initial, onClose, onSaved }: { initial: VendorFull | null; onClose: () => void; onSaved: () => Promise<void> }) {
  const { busy, run } = useSave();
  const [f, setF] = useState({
    name: initial?.name ?? '',
    phone: initial?.phone ?? '',
    email: initial?.email ?? '',
    address: initial?.address ?? '',
    deadline: initial?.deadline ?? '10:30',
    days: initial?.days ?? [1, 2, 3, 4, 5],
    note: initial?.note ?? '',
    active: initial?.active ?? true,
  });
  const [tried, setTried] = useState(false);
  const set = <K extends keyof typeof f>(k: K, v: (typeof f)[K]) => setF((x) => ({ ...x, [k]: v }));
  const errors = {
    name: f.name.trim().length < 2 ? t('lunch.e_name') : null,
    email: f.email.trim() && !/^[^@\s]+@[^@\s]+\.[^@\s]+$/.test(f.email.trim()) ? t('lunch.e_email') : null,
    deadline: !/^([01]\d|2[0-3]):[0-5]\d$/.test(f.deadline) ? t('lunch.e_time') : null,
    days: !f.days.length ? t('lunch.e_days') : null,
  };
  const save = () => {
    setTried(true);
    if (Object.values(errors).some(Boolean)) return;
    void run(async () => {
      await action('lunch.save_vendor', {
        id: initial?.id ?? null,
        name: f.name.trim(),
        phone: f.phone.trim() || null,
        email: f.email.trim() || null,
        address: f.address.trim() || null,
        deadline: f.deadline,
        days: f.days,
        note: f.note.trim() || null,
        active: f.active,
      });
      await onSaved();
    }, t('lunch.vendor_saved'));
  };
  return (
    <Dialog title={initial ? t('lunch.edit_vendor') : t('lunch.add_vendor')} onClose={onClose} size="md" footer={<Foot busy={busy} onClose={onClose} onSave={save} />}>
      <div className="form-stack">
        <Field label={t('lunch.vendor_name')} error={tried ? errors.name : null} required>
          <input className="input" value={f.name} maxLength={80} onChange={(e) => set('name', e.target.value)} data-autofocus />
        </Field>
        <div className="hr-two">
          <Field label={t('lunch.phone')}>
            <input className="input" type="tel" value={f.phone} maxLength={40} onChange={(e) => set('phone', e.target.value)} />
          </Field>
          <Field label={t('lunch.email_field')} error={tried ? errors.email : null}>
            <input className="input" type="email" value={f.email} maxLength={120} onChange={(e) => set('email', e.target.value)} />
          </Field>
        </div>
        <Field label={t('lunch.address')}>
          <input className="input" value={f.address} maxLength={160} onChange={(e) => set('address', e.target.value)} />
        </Field>
        <Field label={t('lunch.deadline')} hint={t('lunch.deadline_hint')} error={tried ? errors.deadline : null} required>
          <input className="input lu-time" type="time" value={f.deadline} onChange={(e) => set('deadline', e.target.value)} />
        </Field>
        <Field label={t('lunch.days')} error={tried ? errors.days : null} required>
          <div className="lu-days" role="group" aria-label={t('lunch.days')}>
            {DAYS.map((n) => {
              const on = f.days.includes(n);
              return (
                <button
                  key={n}
                  type="button"
                  aria-pressed={on}
                  className={`lu-day-pill${on ? ' on' : ''}`}
                  onClick={() => set('days', on ? f.days.filter((x) => x !== n) : [...f.days, n].sort())}
                  title={dayName(n, 'long')}
                >
                  {dayName(n)}
                </button>
              );
            })}
          </div>
        </Field>
        <Field label={t('lunch.vendor_note')} hint={t('lunch.vendor_note_hint')}>
          <input className="input" value={f.note} maxLength={500} onChange={(e) => set('note', e.target.value)} />
        </Field>
        {initial ? (
          <div className="setting-row">
            <div>
              <strong>{t('lunch.takes_orders')}</strong>
              <p className="muted">{t('lunch.takes_orders_hint')}</p>
            </div>
            <Switch checked={f.active} onChange={(x) => set('active', x)} label={t('lunch.takes_orders')} />
          </div>
        ) : null}
      </div>
    </Dialog>
  );
}

function DishForm({ vendor, initial, onClose, onSaved }: { vendor: VendorFull; initial: VendorFull['products'][number] | null; onClose: () => void; onSaved: () => Promise<void> }) {
  const { busy, run } = useSave();
  const [f, setF] = useState({
    name: initial?.name ?? '',
    category: initial?.category ?? '',
    description: initial?.description ?? '',
    price: initial ? plain(initial.price) : '',
    active: initial?.active ?? true,
  });
  const [tried, setTried] = useState(false);
  const set = <K extends keyof typeof f>(k: K, v: (typeof f)[K]) => setF((x) => ({ ...x, [k]: v }));
  const price = parseMoney(f.price);
  const errors = {
    name: f.name.trim().length < 2 ? t('lunch.e_dish') : null,
    price: price === null ? t('lunch.e_price') : null,
  };
  const cats = [...new Set(vendor.products.map((p) => p.category).filter((c): c is string => !!c))];
  const save = () => {
    setTried(true);
    if (Object.values(errors).some(Boolean)) return;
    void run(async () => {
      await action('lunch.save_product', {
        id: initial?.id ?? null,
        vendor: vendor.id,
        name: f.name.trim(),
        category: f.category.trim() || null,
        description: f.description.trim() || null,
        price,
        active: f.active,
      });
      await onSaved();
    }, t('lunch.dish_saved'));
  };
  return (
    <Dialog title={initial ? t('lunch.edit_dish') : t('lunch.add_dish_to', { vendor: vendor.name })} onClose={onClose} size="sm" footer={<Foot busy={busy} onClose={onClose} onSave={save} />}>
      <div className="form-stack">
        <Field label={t('lunch.dish')} error={tried ? errors.name : null} required>
          <input className="input" value={f.name} maxLength={80} onChange={(e) => set('name', e.target.value)} data-autofocus />
        </Field>
        <div className="hr-two">
          <Field label={t('lunch.category')} hint={t('lunch.category_hint')}>
            <input className="input" list="lu-cats" value={f.category} maxLength={40} onChange={(e) => set('category', e.target.value)} />
            <datalist id="lu-cats">
              {cats.map((c) => (
                <option key={c} value={c} />
              ))}
            </datalist>
          </Field>
          <Field label={t('lunch.price')} error={tried ? errors.price : null} required>
            <input className="input" inputMode="decimal" value={f.price} onChange={(e) => set('price', e.target.value)} />
          </Field>
        </div>
        <Field label={t('lunch.description')}>
          <input className="input" value={f.description} maxLength={300} onChange={(e) => set('description', e.target.value)} />
        </Field>
        {initial ? (
          <div className="setting-row">
            <div>
              <strong>{t('lunch.on_menu')}</strong>
              <p className="muted">{t('lunch.on_menu_hint')}</p>
            </div>
            <Switch checked={f.active} onChange={(x) => set('active', x)} label={t('lunch.on_menu')} />
          </div>
        ) : null}
      </div>
    </Dialog>
  );
}

function SettingsForm({ initial, onClose, onSaved }: { initial: { overdraft: number | null; message: string | null }; onClose: () => void; onSaved: () => Promise<void> }) {
  const { busy, run } = useSave();
  const [limit, setLimit] = useState(initial.overdraft !== null);
  const [overdraft, setOverdraft] = useState(initial.overdraft !== null ? plain(initial.overdraft) : '0');
  const [message, setMessage] = useState(initial.message ?? '');
  const [tried, setTried] = useState(false);
  const value = overdraft.trim() ? parseMoney(overdraft) : 0;
  const err = limit && value === null ? t('lunch.e_amount') : null;
  const save = () => {
    setTried(true);
    if (err) return;
    void run(async () => {
      await action('lunch.save_settings', {
        overdraft: limit ? value : null,
        message: message.trim() || null,
      });
      await onSaved();
    }, t('lunch.settings_saved'));
  };
  return (
    <Dialog title={t('lunch.settings')} onClose={onClose} size="sm" footer={<Foot busy={busy} onClose={onClose} onSave={save} />}>
      <div className="form-stack">
        <div className="setting-row">
          <div>
            <strong>{t('lunch.limit')}</strong>
            <p className="muted">{t('lunch.limit_hint')}</p>
          </div>
          <Switch checked={limit} onChange={setLimit} label={t('lunch.limit')} />
        </div>
        {limit ? (
          <Field label={t('lunch.overdraft')} hint={t('lunch.overdraft_hint')} error={tried ? err : null}>
            <input className="input" inputMode="decimal" value={overdraft} onChange={(e) => setOverdraft(e.target.value)} />
          </Field>
        ) : null}
        <Field label={t('lunch.message')} hint={t('lunch.message_hint')}>
          <textarea className="input" rows={2} maxLength={300} value={message} onChange={(e) => setMessage(e.target.value)} />
        </Field>
      </div>
    </Dialog>
  );
}

// ---- managers: report ---------------------------------------------------------------------

function ReportTab({ today }: { today: string }) {
  const [month, setMonth] = useState(today.slice(0, 7));
  const from = `${month}-01`;
  const last = new Date(Date.UTC(Number(month.slice(0, 4)), Number(month.slice(5, 7)), 0)).toISOString().slice(0, 10);
  const to = last > today ? today : last;
  const { data } = useAction<{
    vendors: {
      id: string;
      name: string;
      orders: number;
      dishes: number;
      total: number;
    }[];
    people: { id: string; name: string; orders: number; total: number }[];
    total: number;
  }>('lunch.report', { from, to: to < from ? from : to });
  return (
    <div className="lu-report">
      <div className="lu-day-tools">
        <input className="input lu-date" type="month" value={month} max={today.slice(0, 7)} onChange={(e) => e.target.value && setMonth(e.target.value)} aria-label={t('lunch.month')} />
        {data ? <strong>{t('lunch.month_total', { amount: money(data.total) })}</strong> : null}
        <Button
          size="sm"
          icon="download"
          disabled={!data?.people.length}
          onClick={() =>
            data &&
            downloadCsv(
              `lunch-${month}.csv`,
              [t('lunch.person'), t('lunch.orders'), t('lunch.total')],
              data.people.map((p) => [p.name, p.orders, plain(p.total)]),
            )
          }
        >
          {t('lunch.csv')}
        </Button>
      </div>
      {!data ? (
        <Skeleton h={240} r="var(--radius-lg)" />
      ) : (
        <div className="lu-report-grid">
          <section className="panel">
            <h2>{t('lunch.by_vendor')}</h2>
            <TableWrap>
              <Table
                head={[t('lunch.vendor'), t('lunch.dishes'), t('lunch.total')]}
                empty={t('lunch.no_orders_month')}
                rows={data.vendors.map((v) => ({
                  key: v.id,
                  cells: [v.name, String(v.dishes), money(v.total)],
                }))}
              />
            </TableWrap>
          </section>
          <section className="panel">
            <h2>{t('lunch.by_person')}</h2>
            <TableWrap>
              <Table
                head={[t('lunch.person'), t('lunch.orders'), t('lunch.total')]}
                empty={t('lunch.no_orders_month')}
                rows={data.people.map((p) => ({
                  key: p.id,
                  cells: [p.name, String(p.orders), money(p.total)],
                }))}
              />
            </TableWrap>
          </section>
        </div>
      )}
    </div>
  );
}
